import { DEAL_CLOSED_STATUSES, DEAL_WON_STATUSES, TODAY } from '../../constants';
import { insertDealClosure, insertDealStatusChange } from '../../services/dealActivities';
import { updateDeal } from '../../services/deals';
import { notify } from '../../store/feedback';
import { dealLineIncomplete, dealLines } from '../../utils/budget';
import { isoStr } from '../../utils/dates';
import { becomesWon, statusDatesPatch } from './dealDefaults';

/* Pipeline des affaires : mise à jour, changement de statut (motif obligatoire pour les statuts
   clos, contrôle des lignes vendues pour les statuts gagnés). */
export default function dealPipelineHandlers(deps, api) {
  const { deals, closeReasonModal, session, setDealModal, setDeals, setCloseReasonModal } = deps;

  // Met à jour une affaire (DB + state local) ; une affaire qui devient gagnée passe son contact
  // en « Actif ».
  // Renvoie { error } si la base refuse : l'erreur est affichée et rien n'est changé à l'écran.
  async function saveDealPatch(id, patch) {
    const prev = deals.find((a) => a.id === id);
    const error = (await updateDeal(id, patch))?.error;
    if (error) {
      notify('Affaire non enregistrée : ' + error.message);
      return { error };
    }
    setDeals((list) => list.map((a) => (a.id === id ? { ...a, ...patch } : a)));
    const clientId = patch.client_id ?? prev?.client_id;
    if (patch.statut && becomesWon(prev, patch.statut) && clientId)
      await api.saveContactPatch(clientId, { statut: 'actif' });
  }

  // Déplace une affaire vers un nouveau statut (drag&drop kanban, liste) + log auto
  async function moveDeal(id, newStatus) {
    const aff = deals.find((a) => a.id === id);
    if (!aff || aff.statut === newStatus) return;
    // Les statuts clos demandent un motif → on ouvre la modal au lieu de sauvegarder directement
    if (DEAL_CLOSED_STATUSES.includes(newStatus)) {
      setCloseReasonModal({ dealId: id, newStatus, motif: aff.motif_perte || '' });
      return;
    }
    // Statut gagné (Devis accepté, En cours, Terminé) → toutes les lignes doivent avoir leurs
    // champs "vendu" complets, sinon on bloque et on ouvre la fiche affaire sur le problème.
    if (DEAL_WON_STATUSES.includes(newStatus)) {
      const problemes = dealLines(aff).map(dealLineIncomplete).filter(Boolean);
      if (problemes.length) {
        api.openDeal(aff);
        setDealModal((o) => ({ ...o, statut: newStatus, _gateError: problemes }));
        return;
      }
    }
    const today = isoStr(TODAY);
    const patch = { statut: newStatus, ...statusDatesPatch(aff, newStatus) };
    await saveDealPatch(id, patch);
    await insertDealStatusChange(id, newStatus, session, today);
  }

  // Clôture avec motif : le motif est obligatoire (même règle que la fiche affaire), sinon le bloc
  // « Motifs de perte » du tableau de bord se remplit de « Non renseigné ».
  async function confirmCloseReason() {
    const { dealId, newStatus, motif } = closeReasonModal;
    if (!(motif || '').trim()) return void notify('Choisis un motif de clôture.');
    const today = isoStr(TODAY);
    const patch = {
      statut: newStatus,
      motif_perte: motif.trim(),
      ...statusDatesPatch(
        deals.find((a) => a.id === dealId),
        newStatus,
      ),
    };
    await saveDealPatch(dealId, patch);
    await insertDealClosure(dealId, newStatus, session, today);
    setCloseReasonModal(null);
  }

  return { saveDealPatch, moveDeal, confirmCloseReason };
}
